import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  BuildingFilterDto,
  EmailFilterDto,
  EmailItemDto,
  EmailPdfService,
  EmailService,
  TenantService,
} from '@artemis-software/wr-api';
import { Router } from '@angular/router';
import { NbDateService } from '@nebular/theme';
import { currentTenantId, isInternalTenant } from '@/utils/admin-utils';
import { BehaviorSubject, firstValueFrom, throwError, timeout } from 'rxjs';
import { userToIdReference } from '@/utils/id-reference';
import { tryWithLoading } from '@/utils/async-utils';
import { toSelectedRange } from '@/utils/filter-range-utils';
import { catchError } from 'rxjs/operators';
import TenantTypeEnum = BuildingFilterDto.TenantTypeEnum;

@Component({
  standalone: false,
  selector: 'wr-email-overview-page',
  templateUrl: './email-overview-page.component.html',
  styleUrls: ['./email-overview-page.component.scss']
})
export class EmailOverviewPageComponent {

  private readonly emailService = inject(EmailService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dateService = inject<NbDateService<Date>>(NbDateService);

  isAdmin = isInternalTenant();
  currentTenantId = currentTenantId();
  isInternalTenant = isInternalTenant();
  form!: FormGroup;

  readonly tenantService = inject(TenantService);

  readonly loading$ = new BehaviorSubject<boolean>(false);

  private readonly emailPdfService = inject(EmailPdfService);

  readonly loadEmails = (filter: EmailFilterDto) => this.emailService.findAll(this.prepareFilter(filter));
  readonly getCount = (filter: EmailFilterDto) => this.emailService.getCount(this.prepareFilter(filter));

  senders = new BehaviorSubject<string[]>([]);
  receivers = new BehaviorSubject<string[]>([]);

  senders$ = this.senders.asObservable();
  receivers$ = this.receivers.asObservable();

  prepareFilter(filter: EmailFilterDto) {
    return {
      ...filter,
      selectedRange: toSelectedRange(filter.selectedRange),
      tenantId: this.currentTenantId()
    };
  }

  constructor() {
    const startOfMonth = this.dateService.getMonthStart(this.dateService.today());
    const endOfMonth = this.dateService.getMonthEnd(this.dateService.addMonth(startOfMonth, 1));

    this.form = new FormGroup({
      text: this.formBuilder.control(''),
      selectedRange: this.formBuilder.control({ start: startOfMonth, end: endOfMonth }),
      emailType: this.formBuilder.control(null),
      emailFilter: this.formBuilder.control(''),
      userReference: this.formBuilder.control(null),
      receiverReference: this.formBuilder.control(null),
      user: this.formBuilder.control(null),
      receiver: this.formBuilder.control(null),
    });
    this.form.get('user')?.valueChanges.subscribe((value) => {
      this.form.get('userReference')?.setValue(userToIdReference(value));
    });
    this.form.get('receiver')?.valueChanges.subscribe((value) => {
      this.form.get('receiverReference')?.setValue(userToIdReference(value));
    });

    effect(async () => {
      const isInternalTenant = this.isInternalTenant();
      const currentTenantId = this.currentTenantId();
      if (currentTenantId) {
        if (isInternalTenant) {
          this.senders.next([currentTenantId]);
          const tenants = await firstValueFrom(this.tenantService.findAll({ type: TenantTypeEnum.External }));
          this.receivers.next(tenants.map((tenant) => tenant.id));
        } else {
          const tenants = await firstValueFrom(this.tenantService.findAll({ type: TenantTypeEnum.Internal }));
          this.senders.next(tenants.map((tenant) => tenant.id));
          this.receivers.next([currentTenantId]);
        }
      }
    });
  }

  navigateToEmail(emailItemDto: EmailItemDto) {
    this.router.navigate(['/email', emailItemDto.id]);
  }

  displayReceiver(email: EmailItemDto): string {
    return email.recipients.join(', ');
  }

  readonly exportReceiver = (email: EmailItemDto): string => this.displayReceiver(email);

  onDownloadClick(emailId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.generatePdf(emailId);
  }

  async generatePdf(emailId: string): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      try {
        const blob = await firstValueFrom(
          this.emailPdfService.generatePdf({ emailId }).pipe(
            timeout(60000),
            catchError(error => {
              console.error(
                error.name === 'TimeoutError'
                  ? 'Request timed out. Please try again.'
                  : 'An error occurred:',
                error.message
              );
              return throwError(() => error);
            })
          )
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `email-${emailId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to generate or download PDF:', error);
      }
    });
  }
}
